import React, { useEffect, useMemo, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import axios from "axios";

const API_BASE_URL = "https://admin.shopaholics.ge/api";

/* ===== THEME ===== */
const C = {
  card: "#fff",
  text: "#0f172a",
  dim: "#64748b",
  line: "rgba(0,0,0,.08)",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  blue: "#3b82f6",
  green: "#22c55e",
  red: "#ef4444",
  shadow: "0 8px 30px rgba(2,6,23,.08)",
};
const fadeIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;

/* ===== UI ===== */
const Wrap = styled.div`max-width:1280px;margin:min(3vh,24px) auto;padding:clamp(14px,2.8vw,22px);`;
const Card = styled.div`
  background:${C.card};border:1px solid ${C.line};border-radius:16px;box-shadow:${C.shadow};
  padding:clamp(16px,3vw,22px);animation:${fadeIn} .35s both;
`;
const Title = styled.h2`font-size:clamp(18px,2.4vw,22px);margin:0 0 12px;color:${C.text};font-weight:700;`;
const Toolbar = styled.div`
  display:grid;grid-template-columns:1fr auto auto;gap:10px;align-items:center;margin-bottom:12px;
  @media (max-width:760px){grid-template-columns:1fr}
`;
const SearchInput = styled.input`
  border:1px solid ${C.line};border-radius:12px;padding:10px 12px;font-size:14px;width:100%;
  &:focus{border-color:${C.brand};box-shadow:0 0 0 3px rgba(161,90,90,.12);outline:none}
`;
const Select = styled.select`
  border:1px solid ${C.line};border-radius:12px;padding:10px 12px;font-size:14px;background:#fff;outline:none;
`;
const List = styled.div`display:grid;grid-template-columns:1fr;gap:10px;`;

const ProductItem = styled.div`
  border:1px solid ${C.line};border-radius:12px;padding:12px;
  background:${({stock})=> stock===0? "#ffcccc" : stock<50? "#ffdddd" : stock<=150? "#fff6d8" : "#edf7ee"};
`;
const TopRow = styled.div`display:grid;grid-template-columns:auto 1fr auto;gap:12px;align-items:center;`;
const Img = styled.img`width:52px;height:52px;border-radius:8px;object-fit:cover;border:1px solid ${C.line};`;
const Name = styled.div`font-weight:700;color:${C.text};line-height:1.2;`;
const Meta = styled.div`font-size:12.5px;color:${C.dim};`;
const Btn = styled.button`
  border:0;cursor:pointer;font-weight:700;border-radius:10px;padding:8px 10px;color:#fff;
  ${({tone})=>tone==="edit"&&css`background:${C.blue};`}
  ${({tone})=>tone==="save"&&css`background:${C.green};`}
  ${({tone})=>tone==="cancel"&&css`background:${C.red};`}
  &:hover{filter:brightness(.96)}
`;
const Row = styled.div`display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-top:8px;`;
const Label = styled.label`font-size:12.5px;color:${C.dim};`;
const Input = styled.input`
  border:1px solid ${C.line};border-radius:10px;padding:8px 10px;font-size:14px;min-width:120px;
  &:focus{border-color:${C.brand};box-shadow:0 0 0 3px rgba(161,90,90,.12);outline:none}
`;
const Badge = styled.span`
  display:inline-block;border:1px dashed ${C.line};border-radius:8px;padding:4px 8px;font-size:12px;color:${C.dim};
  background:#fafafa;margin-left:6px;
`;
const SubTitle = styled.div`margin-top:10px;font-weight:700;`;
const Table = styled.table`
  width:100%;border-collapse:collapse;margin-top:6px;font-size:14px;
  th,td{border-bottom:1px solid ${C.line};padding:8px 10px;text-align:left}
  th{font-weight:600;font-size:12.5px;color:${C.dim};background:#fafafa}
`;
const Pagination = styled.div`display:flex;gap:8px;align-items:center;justify-content:flex-end;margin-top:12px;flex-wrap:wrap;`;
const PageBtn = styled.button`
  min-width:36px;height:36px;padding:0 10px;border-radius:10px;border:1px solid ${C.line};background:#fff;cursor:pointer;
  ${({active})=>active&&css`background:${C.brand};color:#fff;border-color:${C.brandDark};`}
`;

/* ===== Helpers ===== */
const PERFUME_ONLY = "perfume";
const PER_ML_CATS = new Set(["skin-care","hair-and-body-care","atomizers"]);

/* ---------- STRICT strategy helpers: only units|full ---------- */
function normStrategyUF(s) {
  const v = (s ?? "").toString().trim().toLowerCase();
  return (v === "units") ? "units" : "full";
}
function strategyOfRowUF(ml, isPerMlCategory) {
  if (isPerMlCategory) return "units";
  return normStrategyUF(ml?.stock_strategy ?? ml?.strategy);
}
function qtyBindingUF(ml, isPerMlCategory) {
  const strat = strategyOfRowUF(ml, isPerMlCategory);
  if (strat === "units") {
    return { target: "unit_stock", value: ml.unit_stock ?? 0, disabled: false };
  }
  return { target: "stock", value: ml.stock ?? 0, disabled: true };
}

export default function StockManager(){
  const [items,setItems]=useState([]);
  const [total,setTotal]=useState(0);
  const [page,setPage]=useState(1);
  const [pageSize,setPageSize]=useState(20);
  const [search,setSearch]=useState("");

  const [editingId,setEditingId]=useState(null);
  const [fullStock,setFullStock]=useState("");     // pooled ml total (perfume)
  const [mls,setMls]=useState([]);                 // ml rows (may mix strategies)
  const [variants,setVariants]=useState([]);       // accessories

  const totalPages = useMemo(()=>Math.max(1, Math.ceil(total/pageSize)),[total,pageSize]);
  const pagerButtons = useMemo(()=>{
    const win=7, start=Math.max(1,Math.min(page-Math.floor(win/2), totalPages-(win-1)));
    return Array.from({length:Math.min(win,totalPages)},(_,i)=>start+i);
  },[totalPages,page]);

  useEffect(()=>{ fetchData(); /* eslint-disable-next-line */ },[page,pageSize,search]);

  async function fetchData(){
    try{
      const params = { page, page_size: pageSize, search: search.trim()||undefined };
      const {data} = await axios.get(`${API_BASE_URL}/get_perfumes.php`, { params, withCredentials:true });
      setItems(data.items||[]); setTotal(data.total||0);
      const newPages = Math.max(1, Math.ceil((data.total||0)/pageSize));
      if(page>newPages) setPage(newPages);
    }catch(e){ console.error(e); setItems([]); setTotal(0); }
  }

  function startEdit(p){
    if(editingId===p.product_id){ setEditingId(null); setFullStock(""); setMls([]); setVariants([]); return; }
    setEditingId(p.product_id);
    const cat = (p.category||"").toLowerCase();

    if (cat === PERFUME_ONLY) {
      setFullStock(p.full_stock ?? "");
      const isPerMl = false;
      setMls(Array.isArray(p.milliliters) ? p.milliliters.map(ml => ({
        id: ml.id,
        size: ml.size,
        price: ml.price,
        sale_price: ml.sale_price ?? null,
        stock_strategy: strategyOfRowUF(ml, isPerMl), // "units" | "full"
        stock: ml.stock ?? null,
        unit_stock: ml.unit_stock ?? null,
      })) : []);
      setVariants([]);
    }
    else if (PER_ML_CATS.has(cat)) {
      setFullStock("");
      const isPerMl = true;
      setMls(Array.isArray(p.milliliters) ? p.milliliters.map(ml => ({
        id: ml.id,
        size: ml.size,
        price: ml.price,
        sale_price: ml.sale_price ?? null,
        stock_strategy: strategyOfRowUF(ml, isPerMl), // always "units"
        stock: null,
        unit_stock: ml.unit_stock ?? ml.stock ?? 0,
      })) : []);
      setVariants([]);
    }
    else {
      setFullStock(""); setMls([]);
      setVariants(Array.isArray(p.accessory_variants)? p.accessory_variants.map(v=>({
        id:v.id, color_ka:v.color_ka, color_en:v.color_en, size_label:v.size_label, sku:v.sku,
        price:v.price, stock:v.stock, is_active:v.is_active, images:v.images||[],
        sale_price: v.sale_price ?? v.sale_p ?? null
      })) : []);
    }
  }

  // Full pooled ML change → განახლდეს მხოლოდ იმ რიგებზე, რომლებსაც strategy === "full"
  function onFullStockChange(e){
    const raw = e.target.value; setFullStock(raw);
    const totalMl = raw === "" ? null : parseInt(raw, 10);

    setMls(prev => {
      if (totalMl === null || isNaN(totalMl)) {
        return prev.map(m => strategyOfRowUF(m, false) === "full" ? ({ ...m, stock: 0 }) : m);
      }
      return prev.map(m => {
        if (strategyOfRowUF(m, false) !== "full") return m;
        const size = Number(m.size) || 0;
        const units = size > 0 ? Math.floor(totalMl / size) : 0;
        return { ...m, stock: units };
      });
    });
  }

  function onMlChange(i, field, value){
    setMls(prev=>{
      const cp=[...prev]; cp[i]={...cp[i],[field]:value};
      return cp;
    });
  }
  function onVariantChange(i, field, value){
    setVariants(prev=>{ const cp=[...prev]; cp[i]={...cp[i],[field]:value}; return cp; });
  }

  async function save(){
    const user = JSON.parse(localStorage.getItem("user"));
    if(!user?.email){ alert("Unauthorized"); return; }

    const product = items.find(x=>x.product_id===editingId);
    if(!product){ alert("Product not found"); return; }

    const cat = (product.category||"").toLowerCase();
    const isPerfumeOnly = cat === PERFUME_ONLY;
    const isPerMl       = PER_ML_CATS.has(cat);

    // ml payload per-row strictly by strategy
 const mlPayload = (isPerfumeOnly || isPerMl)
   ? mls.map(m => {
       const strat = strategyOfRowUF(m, isPerMl); // "units" | "full"
       const size  = Number(m.size) || 0;
       const price = m.price === "" ? 0 : Number(m.price);
       const row = {
         id: m.id ?? null,                 // 👈 ვუგზავნით ID-ს, რომ update ზუსტად ვითარდეს
         stock_strategy: strat,            // 👈 ვუგზავნით სტრატეგიას
         size,
         price
       };
       if (strat === "units") {
         row.unit_stock = Number(m.unit_stock ?? 0) || 0;
       } else {
         row.stock = Number(m.stock ?? 0) || 0;
       }
       return row;
     })
   : null;

    const payload = {
      product_id: editingId,
      email: user.email,
      ...(isPerfumeOnly
        ? { full_stock: fullStock===""? null : parseInt(fullStock,10), milliliters: mlPayload }
        : isPerMl
          ? { full_stock: null, milliliters: mlPayload }
          : { variants: variants.map(v=>({
              id: v.id,
              stock: v.stock===""? 0 : Number(v.stock),
              price: v.price===""? null : Number(v.price),
            }))
            }
      )
    };

    try{
      const {data} = await axios.post(`${API_BASE_URL}/stockedit.php`, payload, { withCredentials:true });
      if(data.error){ alert(data.error); return; }
      alert("✅ Stock updated");
      setEditingId(null); setFullStock(""); setMls([]); setVariants([]);
      fetchData();
    }catch(e){ console.error(e); alert("❌ Update failed"); }
  }

  return (
    <Wrap>
      <Card>
        <Title>Stock Manager</Title>
        <Toolbar>
          <SearchInput
            placeholder="Search by name or product_id…"
            value={search}
            onChange={(e)=>{ setPage(1); setSearch(e.target.value); }}
          />
          <Select value={pageSize} onChange={e=>{ setPage(1); setPageSize(Number(e.target.value)); }}>
            {[10,20,30,50,100].map(n=><option key={n} value={n}>{n} / page</option>)}
          </Select>
          <div style={{fontSize:12.5,color:C.dim}}>{total.toLocaleString()} items</div>
        </Toolbar>

        <List>
          {items.map(p=>{
            const cover = p.cover || (p.imageUrls?.[0]) || p.image || "";
            const cat = (p.category||"").toLowerCase();
            const isPerfumeOnly = cat === PERFUME_ONLY;
            const isPerMl = PER_ML_CATS.has(cat);

            return (
              <ProductItem key={p.product_id} stock={Number(p.display_stock||0)}>
                <TopRow>
                  <Img src={cover || ""} alt="" />
                  <div>
                    <Name>{p.name}</Name>
                    <Meta>ID: {p.product_id} • Brand: {p.brand||"-"} • Stock: {p.display_stock ?? 0} • Cat: {p.category}</Meta>
                  </div>
                  <Btn tone="edit" onClick={()=>startEdit(p)}>{editingId===p.product_id?"Close":"Edit"}</Btn>
                </TopRow>

                {editingId===p.product_id && (
                  <>
                    {(isPerfumeOnly || isPerMl) ? (
                      <>
                        {isPerfumeOnly && (
                          <Row>
                            <Label>Full stock (ml total):</Label>
                            <Input type="number" value={fullStock} onChange={onFullStockChange}/>
                            <Badge>full → auto “stock”, units → manual “unit_stock”</Badge>
                            <Btn tone="save" onClick={save} style={{marginLeft:8}}>Save</Btn>
                            <Btn tone="cancel" onClick={()=>{ setEditingId(null); setFullStock(""); setMls([]); }} style={{marginLeft:4}}>Cancel</Btn>
                          </Row>
                        )}

                        {isPerMl && (
                          <Row>
                            <Badge>per-ML category → edit unit_stock</Badge>
                            <Btn tone="save" onClick={save} style={{marginLeft:8}}>Save</Btn>
                            <Btn tone="cancel" onClick={()=>{ setEditingId(null); setMls([]); }} style={{marginLeft:4}}>Cancel</Btn>
                          </Row>
                        )}

                        <SubTitle>Milliliters</SubTitle>
                        <Table>
                          <thead>
                            <tr>
                              <th>Size (ml)</th>
                              <th>Strategy</th>
                              <th>Qty</th>
                              <th>Price</th>
                              <th>Sale (RO)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mls.map((ml, i) => {
                              const strat = strategyOfRowUF(ml, isPerMl); // "units" | "full"
                              const { target, value, disabled, label } = qtyBindingUF(ml, isPerMl);
                              return (
                                <tr key={ml.id ?? i}>
                                  <td><Input type="number" value={ml.size ?? 0} readOnly disabled/></td>
                                  <td>{strat}</td>
                                  <td>
                                    <div style={{display:"flex", flexDirection:"column", gap:4}}>
                                      <Input
                                        type="number"
                                        value={value}
                                        disabled={disabled}
                                        onChange={e => onMlChange(i, target, e.target.value)}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <Input
                                      type="number"
                                      value={ml.price ?? 0}
                                      onChange={e=>onMlChange(i,"price",e.target.value)}
                                    />
                                  </td>
                                  <td><Input type="number" value={ml.sale_price ?? ""} readOnly/></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </>
                    ) : (
                      <>
                        <Row>
                          <Btn tone="save" onClick={save}>Save</Btn>
                          <Btn tone="cancel" onClick={()=>{ setEditingId(null); setVariants([]); }}>Cancel</Btn>
                        </Row>

                        <SubTitle>Accessory Variants</SubTitle>
                        <Table>
                          <thead>
                            <tr>
                              <th>Variant</th>
                              <th>Stock</th>
                              <th>Price</th>
                              <th>Sale (RO)</th>
                              <th>Images</th>
                            </tr>
                          </thead>
                          <tbody>
                            {variants.map((v,i)=>{
                              const label=[v.color_ka||null, v.size_label||null, v.sku||null].filter(Boolean).join(" • ");
                              return (
                                <tr key={v.id}>
                                  <td>{label || v.id}</td>
                                  <td><Input type="number" value={v.stock ?? 0} onChange={e=>onVariantChange(i,"stock",e.target.value)} /></td>
                                  <td><Input type="number" value={v.price ?? 0} onChange={e=>onVariantChange(i,"price",e.target.value)} /></td>
                                  <td><Input type="number" value={v.sale_price ?? ""} readOnly /></td>
                                  <td style={{whiteSpace:"nowrap"}}>
                                    {(v.images||[]).slice(0,3).map((src,idx)=><img key={idx} src={src} alt="" style={{width:32,height:32,objectFit:"cover",borderRadius:6,border:"1px solid #eee",marginRight:6}}/>)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </>
                )}
              </ProductItem>
            );
          })}
          {items.length===0 && <div style={{color:C.dim}}>Nothing found…</div>}
        </List>

        <Pagination>
          <PageBtn onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>‹</PageBtn>
          {pagerButtons.map(n=><PageBtn key={n} active={n===page} onClick={()=>setPage(n)}>{n}</PageBtn>)}
          <PageBtn onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>›</PageBtn>
        </Pagination>
      </Card>
    </Wrap>
  );
}
